import type { DateValue } from '@internationalized/date';
import { create } from 'zustand';
import type { OmitFunctions } from '@/types';
import { createSelectors } from './selector';

type ContentReviewState = {
  search: string;
  createdAt: DateValue | undefined;
  createdBy: number | null;
  actionedBy: number | null;
  recordIds: number[];
  type: number | null;
  state: number | null;
  withRecords: boolean | null;
  // Feedback modal state
  isFeedbackModalOpen: boolean;
  feedbackReviewId: string | null;
  feedbackValue: string;
  selectedIds: (string | number)[];
  setSelectedIds: (ids: (string | number)[]) => void;
  clearSelectedIds: () => void;
  feedbackType: 'comment' | 'reason';
};

type ContentReviewActions = {
  setSearch: (value: string) => void;
  setCreatedAt: (value: DateValue | undefined) => void;
  setCreatedBy: (value: number | null) => void;
  setActionedBy: (value: number | null) => void;
  setRecordIds: (value: number[]) => void;
  setType: (value: number | null) => void;
  setState: (value: number | null) => void;
  setWithRecords: (value: boolean | null) => void;
  resetFilters: () => void;
  // Feedback modal actions
  openFeedbackModal: (reviewId: string, initialValue: string, type: 'comment' | 'reason') => void;
  closeFeedbackModal: () => void;
  setFeedbackValue: (value: string) => void;
  setSelectedIds: (ids: (string | number)[]) => void;
  clearSelectedIds: () => void;
};

const initialState: OmitFunctions<ContentReviewState> = {
  search: '',
  createdAt: undefined,
  createdBy: null,
  actionedBy: null,
  recordIds: [],
  type: 1,
  state: 0,
  withRecords: false,
  isFeedbackModalOpen: false,
  feedbackReviewId: null,
  feedbackValue: '',
  selectedIds: [],
  feedbackType: 'comment',
};

const contentReviewSlice = create<ContentReviewState & ContentReviewActions>((set) => ({
  ...initialState,
  setSearch: (value) => set({ search: value }),
  setCreatedAt: (value) => set({ createdAt: value }),
  setCreatedBy: (value) => set({ createdBy: value }),
  setActionedBy: (value) => set({ actionedBy: value }),
  setRecordIds: (value) => set({ recordIds: value }),
  setType: (value) => set({ type: value }),
  setState: (value) => set({ state: value }),
  setWithRecords: (value) => set({ withRecords: value }),
  resetFilters: () => set(initialState),
  openFeedbackModal: (reviewId, initialValue, type) =>
    set({
      isFeedbackModalOpen: true,
      feedbackReviewId: reviewId,
      feedbackValue: initialValue,
      feedbackType: type,
    }),
  closeFeedbackModal: () => set({ isFeedbackModalOpen: false, feedbackReviewId: null, feedbackValue: '' }),
  setFeedbackValue: (value) => set({ feedbackValue: value }),
  setSelectedIds: (ids) => set({ selectedIds: ids }),
  clearSelectedIds: () => set({ selectedIds: [] }),
}));

export const useContentReview = createSelectors(contentReviewSlice);
